// The identifier block: every number an order carries, on every screen that
// shows an order. #304's gate D13/D15/D16 and #545, Session 79.14d.
//
// It sits in src/components/ rather than in purchasing/ or inventory/ because it
// is used by FIVE surfaces across three domains — the Purchasing Queue, the
// Requests inbox, Invoicing, the receive page and the receive sheet. The whole
// argument for one component is that the block is the same shape everywhere: a
// person moving between Jennifer's screen and the pallet reads the numbers in
// the same order in the same place.
//
// WHY IT EXISTS. #545: Stede generates the PO number, the buyer reads it down the
// phone to the vendor, and it then appears on no card, in no list and in neither
// detail panel — only inside the Mark as ordered sheet, which closes when the
// order is confirmed. The Requests inbox meanwhile advertises PO search in its
// own placeholder and shows no PO number on what it returns. A search that finds
// a record the record cannot confirm is worse than no search, which is why D13
// is a precondition of D6 rather than a nicety.

import React from 'react';
import { orderIdentifiers } from '../lib/orderIdentifiers';

// D15. THE MUTED GREY IS NOT THE ONE THE GATE DREW, and the difference was
// measured rather than eyeballed. The gate specified #9A9A9A on #FAFAFA, which
// computes to 2.70 against the 4.5 this project committed to at Session 78w —
// so the chip that exists to say "somebody checked and there is no number here"
// would have been the one hardest to read. #6E6E6E measures 4.89 on the same
// ground and is still plainly subordinate to the #0F0F0F real values, so the
// hierarchy the gate wanted survives. See #304's gate, D15, amended at 79.14d.
const MUTED = '#6E6E6E';

const chipBase = {
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: 4,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '.02em',
  color: '#5C5C5C',
  background: '#FFFFFF',
  border: '1px solid #DCDCDC',
  borderRadius: 4,
  padding: '1px 6px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  whiteSpace: 'nowrap',
};

function chipStyle(state) {
  if (state === 'lead') {
    return { ...chipBase, borderColor: '#EF9F27', background: '#FFF8EC', color: '#8A5A08' };
  }
  if (state === 'none') {
    return { ...chipBase, color: MUTED, background: '#FAFAFA', borderStyle: 'dashed' };
  }
  return chipBase;
}

function valueStyle(state) {
  if (state === 'lead') return { color: '#8A5A08', fontWeight: 700 };
  // A dash is a fact, not a number — so it does not get the weight a number gets.
  if (state === 'none') return { color: MUTED, fontWeight: 400 };
  return { color: '#0F0F0F', fontWeight: 700 };
}

// `leadKey` marks the identifier the person searched by, so the eye goes straight
// to the number they were holding. Pass null when no search is running.
//
// `only` narrows the block to a subset for a surface where all six do not fit.
//
// `omitEmpty` drops the ones this record does not have, and it is COLBY'S
// RULING at Session 79.14d rather than a loophole in D15 — see #546. He read
// the built cards and said it plainly: a card should show "the numbers that
// have actually been tied to an order at the point they're being rendered."
//
// WHY THAT IS SAFE, AND WHY IT IS BETTER THAN THE STAGE-BASED RULE I PROPOSED.
// D15's dash exists so a field that silently vanishes cannot read as a
// rendering fault. That risk lives on a DETAIL PANEL, where somebody is
// studying one record and an absence is ambiguous. A CARD IS A SCANNING
// SURFACE: you are comparing rows, and a row with fewer chips reads as a row
// with fewer numbers. #546 recommended branching on the order's STAGE — work
// order before ordering, all five after — and his rule needs no stage at all,
// which is simpler and has no edge to get wrong.
//
// THE SPLIT IS THE WHOLE THING AND IT MUST NOT DRIFT: cards omit, panels never
// do. D13's promise — the record shows the identifiers you did NOT search by —
// is kept on the panels and the pallet sheet, which is where a person goes to
// confirm they have the right order. Default false, so a surface opts in
// explicitly rather than inheriting it.
//
// It cannot break the search mark: a lead chip is BY DEFINITION a number that
// exists on the record, so it always survives the filter.
export default function OrderIdentifiers({ req, leadKey = null, only = null, omitEmpty = false, style = null, title = null }) {
  let fields = orderIdentifiers(req);
  if (only) fields = fields.filter(f => only.includes(f.key));
  if (omitEmpty) fields = fields.filter(f => f.value != null);
  if (fields.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', ...(style || {}) }}>
      {title && (
        <span style={{
          fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em',
          color: '#5C5C5C', fontWeight: 700, marginRight: 2,
        }}>{title}</span>
      )}
      {fields.map(f => {
        const state = f.value == null ? 'none' : (leadKey === f.key ? 'lead' : 'has');
        return (
          <span
            key={f.key}
            style={chipStyle(state)}
            // A screen reader gets a sentence rather than two adjacent fragments,
            // and "not recorded" rather than a dash it would read as a hyphen.
            aria-label={`${f.label}: ${f.value == null ? 'not recorded' : f.value}`}
          >
            <span aria-hidden="true">{f.label}</span>
            <b aria-hidden="true" style={valueStyle(state)}>{f.value == null ? '—' : f.value}</b>
          </span>
        );
      })}
    </div>
  );
}
